import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex justify-content-center mt-14 mx-1"
}
const _hoisted_2 = { class: "col lg:col-6 xl:col-5" }

import { useRootStore } from '@/store';
import { Alert, Modal } from '@/store/classes';
import {ExternalFacilityType} from "@/store/enums";
import { computed, onMounted, ref } from 'vue';
import axios from "axios";
import { useRouter } from 'vue-router';
import { useFormValidation } from '@/components/common/FormValidation';
import { z } from 'zod';
import { PasswordSchema, RequiredStringSchema } from "@/components/common/schemas";
import PortalAppHeader from '@/components/portal/PortalAppHeader.vue3.vue';
import FormField from "@/components/common/FormField.vue3.vue"
import { useI18n } from 'vue-i18n';

export default /*@__PURE__*/_defineComponent({
  __name: 'ResetLogin.vue3',
  props: {
    resetKey: {}
  },
  setup(__props: any) {

const { t } = useI18n();

const props = __props;

const router = useRouter();
const rootStore = useRootStore();

const showModal = async (modal: Modal) => rootStore.dispatch('showModal', modal);
const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);

const isLoading = ref(true);
const savePending = ref(false);
const expiredReset = ref(false);
const resetConfirmation = ref({
    securityQuestionIndex: undefined,
    securityQuestion: ''
});
const securityQuestionAnswer = ref('');
const password = ref('');
const confirmPassword = ref('');

const passwordResetSchema = z.object({
    securityQuestionAnswer: RequiredStringSchema,
    password: RequiredStringSchema.and(PasswordSchema),
    confirmPassword: RequiredStringSchema,
}).superRefine((val, ctx) => {  
    if (val.password !== val.confirmPassword) {
        ctx.addIssue({
            message: t('message.validation.confirmNewPasswordMatch'),
            code: 'custom',
            path: ['confirmPassword']
        });
    }
});

const validationState = useFormValidation<z.infer<typeof passwordResetSchema>>(
    passwordResetSchema,
    computed(() => ({
        securityQuestionAnswer:securityQuestionAnswer.value,
        password:password.value,
        confirmPassword: confirmPassword.value,
    })),
    false
);

onMounted(async ()=>{
    try {
        await showLoader('reset-login');
        const resetResponse = await axios.get(`/auth/reset/${props.resetKey}`);
        resetConfirmation.value = resetResponse.data;
        if (resetResponse.status === 204) {
            expiredReset.value = true; 
        } else if (resetResponse.status !== 200) {
            throw 'Invalid password reset request';
        }
    } catch (e) {
        console.error(e);
        await showAlert({
            text: 'Unable to find password reset request, please submit a new request',
            type: 'error'
        });
        router.push({
            path: '/login',
            query: {
                facilityType: ExternalFacilityType.NhMessaging
            }
        });
    } finally {
        await hideLoader('reset-login');
        isLoading.value = false;
    }
})

async function resetLogin(): Promise<void> {
    if (savePending.value) return;
    savePending.value = true;
            
    if (!validationState.validate()) {
        savePending.value = false;
        return;
    }
            
    try {
        await showLoader('reset-login');
        await axios.put(`/auth/reset/${props.resetKey}`, {
            securityQuestionIndex: resetConfirmation.value.securityQuestionIndex,
            securityQuestionAnswer: securityQuestionAnswer.value,
            newPassword: password.value 
        });
    } catch (e: any) {
        const text = e.response?.status === 400 && e.response?.data
            ? e.response.data
            : 'Unable to reset password, please try again';
        await showAlert({text, type: 'error'});
        return;
    } finally {
        await hideLoader('reset-login');
        savePending.value = false;
    }
    await showAlert({text: 'Password reset, log in to continue', type: 'success'});
    router.push({
        path: '/login',
        query: {
            facilityType: ExternalFacilityType.NhMessaging
        }
    });
}

return (_ctx: any,_cache: any) => {
  const _component_p_button = _resolveComponent("p-button")!
  const _component_p_card = _resolveComponent("p-card")!
  const _component_p_input_text = _resolveComponent("p-input-text")!
  const _component_p_divider = _resolveComponent("p-divider")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(PortalAppHeader),
    (!isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            (expiredReset.value)
              ? (_openBlock(), _createBlock(_component_p_card, {
                  key: 0,
                  class: "form-card"
                }, {
                  title: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                    _createTextVNode(" Password Reset Expired ", -1)
                  ]))]),
                  content: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("p", null, " This reset link has expired. Click below to submit a new password reset request. ", -1)
                  ]))]),
                  footer: _withCtx(() => [
                    _createVNode(_component_p_button, {
                      label: "Primary",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (showModal({ name: 'ResetLoginModal' })))
                    }, {
                      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                        _createTextVNode(" Reset Password ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
              : (_openBlock(), _createBlock(_component_p_card, {
                  key: 1,
                  class: "form-card"
                }, {
                  title: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                    _createTextVNode(" Reset Password ", -1)
                  ]))]),
                  content: _withCtx(() => [
                    _createElementVNode("p", null, [
                      _createElementVNode("strong", null, _toDisplayString(_unref(t)('labels.user.securityQuestion')) + ":", 1),
                      _createTextVNode(" " + _toDisplayString(resetConfirmation.value.securityQuestion), 1)
                    ]),
                    _createVNode(FormField, {
                      "validation-state": _unref(validationState).result.value.securityQuestionAnswer,
                      "label-key": "labels.user.securityQuestionAnswer",
                      required: ""
                    }, {
                      default: _withCtx(({ onBlur }) => [
                        _createVNode(_component_p_input_text, {
                          modelValue: securityQuestionAnswer.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((securityQuestionAnswer).value = $event)),
                          onBlur: onBlur
                        }, null, 8, ["modelValue", "onBlur"])
                      ]),
                      _: 1
                    }, 8, ["validation-state"]),
                    _createVNode(_component_p_divider),
                    _createVNode(FormField, {
                      "validation-state": _unref(validationState).result.value.password,
                      "label-key": "labels.user.newPassword",
                      required: ""
                    }, {
                      default: _withCtx(({ onBlur }) => [
                        _createVNode(_component_p_input_text, {
                          modelValue: password.value,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((password).value = $event)),
                          type: "password",
                          onBlur: onBlur
                        }, null, 8, ["modelValue", "onBlur"])
                      ]),
                      _: 1
                    }, 8, ["validation-state"]),
                    _createVNode(FormField, {
                      "validation-state": _unref(validationState).result.value.confirmPassword,
                      "label-key": "labels.user.confirmNewPassword",
                      required: ""
                    }, {
                      default: _withCtx(({ onBlur }) => [
                        _createVNode(_component_p_input_text, {
                          modelValue: confirmPassword.value,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((confirmPassword).value = $event)),
                          type: "password",
                          onBlur: onBlur
                        }, null, 8, ["modelValue", "onBlur"])
                      ]),
                      _: 1
                    }, 8, ["validation-state"])
                  ]),
                  footer: _withCtx(() => [
                    _createVNode(_component_p_button, {
                      label: "Primary",
                      onClick: resetLogin
                    }, {
                      default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                        _createTextVNode(" Reset ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})